import { TabStyles } from '@tableau/widgets';
import * as React from 'react';
import { style } from 'typestyle';
import { ServiceLocator } from '../../../../app/ServiceLocator';
import { Messages } from '../../../../codegen/Localize';
import { FormikCheckbox } from '../../../../commonInfra/components/FormikCheckbox';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../../../sessionManagement/SessionManagementService';
import { SiteAuthenticationSettingsService } from '../../../../ts/services/SiteAuthenticationSettingsService';
import { SettingsSection } from '../../../components/common/SettingsSection';
import { SiteSettingsForSiteAdminProps } from '../ReactSiteSettingsForSiteAdmin';
import { componentRegistryEntry } from '../SettingsComponentRegistryFunctions';

export type SiteInviteNotificationSectionProps = SiteSettingsForSiteAdminProps<'siteInviteEmailEnabled'>;
export type SiteInviteNotificationSectionInternalProps = {
              serverService: SessionManagementService;
              siteAuthenticationSettingsService: SiteAuthenticationSettingsService;
            } & SiteInviteNotificationSectionProps;

export function SiteInviteNotificationSectionInternal(props: SiteInviteNotificationSectionInternalProps) {
  const siteInviteEmailEnabledForServer = props.serverService.isSiteInviteEmailEnabledForServer();
  const isSiteInviteNotificationUnavailable = useIsSiteNotificationUnavailable(siteInviteEmailEnabledForServer, props.siteAuthenticationSettingsService);
  const spacer = style({
    paddingBottom: TabStyles.Spacing.MediumSpace,
    paddingLeft: TabStyles.Spacing.ExtraLargeSpace,
    fontStyle: 'italic'
  });

  if (!siteInviteEmailEnabledForServer) {
    return null;
  }
  return <SettingsSection title={Messages.siteInviteSettingSection()} description={Messages.siteInviteSettingsDetails()}>
      <FormikCheckbox
        name={'settings.siteInviteEmailEnabled'}
        label={Messages.siteInviteSettingsEnabled()}
        testId={'site-invite-checkbox'}
        disabled={isSiteInviteNotificationUnavailable}
      />
      {
        isSiteInviteNotificationUnavailable &&
        <div className={spacer} id='site-invite-notification-unavailable-id'>
          {Messages.siteInviteSettingsDisabledWarning()}
        </div>
      }
    </SettingsSection>;
}

function useIsSiteNotificationUnavailable(siteInviteEmailEnabledForServer: boolean, siteAuthenticationSettingsService: SiteAuthenticationSettingsService) {
  const [isSiteNotificationUnavailable, setisSiteNotificationUnavailable] = React.useState<boolean>(null);
  React.useEffect(() => {
    if (siteInviteEmailEnabledForServer && isSiteNotificationUnavailable === null) {
      const authSettings = siteAuthenticationSettingsService.getSiteAuthenticationSettings();
      authSettings.then(authSettings => setisSiteNotificationUnavailable(!siteAuthenticationSettingsService.isAuthenticationSSO(authSettings)));
    }
  }, [siteInviteEmailEnabledForServer]);
  return isSiteNotificationUnavailable;
}

/**
 * A wrapper component which provides the dependencies to the internal component
 * @param props
 * @constructor
 */
export function SiteInviteNotificationSection(props: SiteInviteNotificationSectionProps) {
  const deps: SiteInviteNotificationSectionInternalProps = {
    serverService: getSessionManagementServiceInstance(),
    siteAuthenticationSettingsService: ServiceLocator.get<SiteAuthenticationSettingsService>('SiteAuthenticationSettingsService'),
    settings: props.settings,
    siteId: props.siteId
  };
  return <SiteInviteNotificationSectionInternal {...deps} />;
}

/* for registering the component in settings registry */
export const SiteInviteNotificationSpecs = componentRegistryEntry({
  name: 'SiteInviteNotificationSection',
  component: SiteInviteNotificationSection
});

